"use client";

import { useEffect, useState } from "react";
import type { Section } from "@/lib/doc";

/**
 * The contents list, with the current section marked.
 *
 * An IntersectionObserver rather than a scroll handler: the browser does the
 * work off the main thread and hands back only the crossings, which is the
 * difference between a listener firing on every frame of a Lenis scroll and
 * one firing seventeen times over the length of the document.
 *
 * The root margin pins the "active" band to a strip near the top of the
 * viewport. Without it, a long section and a short one both count as visible
 * for most of their length and the marker flickers between them.
 *
 * Anchors stay plain `href="#id"`. Lenis intercepts in-page links itself with
 * the nav offset already applied (see components/providers/SmoothScroll), so
 * adding our own scroll handling here would fight it.
 */

export default function Toc({ sections }: { sections: Section[] }) {
  const [active, setActive] = useState(sections[0]?.id ?? "");

  useEffect(() => {
    const seen = new Map<string, boolean>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) seen.set(entry.target.id, entry.isIntersecting);
        const first = sections.find((s) => seen.get(s.id));
        if (first) setActive(first.id);
      },
      { rootMargin: "-88px 0px -66% 0px", threshold: 0 },
    );

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sections]);

  return (
    <nav aria-label="Contents" className="text-[13.5px]">
      <p className="mb-4 font-mono text-[11.5px] uppercase tracking-[0.18em] text-faint">
        Contents
      </p>
      <ol className="space-y-1.5">
        {sections.map((section) => {
          const current = section.id === active;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                aria-current={current ? "location" : undefined}
                className={[
                  "flex gap-2.5 rounded-md py-0.5 transition-colors duration-150",
                  "[transition-timing-function:var(--ease-out)]",
                  current ? "text-paper" : "text-faint hover:text-quiet",
                ].join(" ")}
              >
                <span
                  className={[
                    "w-4 shrink-0 text-right font-mono text-[11.5px] tabular-nums",
                    current ? "text-cyan-live" : "text-hairline",
                  ].join(" ")}
                >
                  {section.n}
                </span>
                <span className="leading-snug">{section.title}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
